import { GlLink, GlIcon, GlDisclosureDropdown, GlDisclosureDropdownGroup } from '../../../../../../node_modules/@gitlab/ui/src/index.js?vue3';
import { getHTTPProtocol, mergeUrlParams } from '~/lib/utils/url_utility';
import { __, sprintf } from '~/locale';
import { GO_TO_PROJECT_WEBIDE, keysFor } from '~/behaviors/shortcuts/keybindings';
import { InternalEvents } from '../../../tracking/index.js?vue3';
import CodeDropdownCloneItem from './code_dropdown_clone_item.vue?vue3';
import CodeDropdownDownloadItems from './code_dropdown_download_items.vue?vue3';
import CodeDropdownIdeItem from './code_dropdown_ide_item.vue?vue3';
import { VSCODE_BASE_URL, JETBRAINS_BASE_URL, CODE_DROPDOWN_CLICK, COPY_SSH_CLONE_URL, COPY_HTTPS_CLONE_URL, OPEN_VSCODE_SSH, OPEN_VSCODE_HTTPS, OPEN_INTELLIJ_SSH, OPEN_INTELLIJ_HTTPS, IDE_TYPE_VSCODE, IDE_TYPE_INTELLIJ, OPEN_CUSTOM_IDE_LINK, EXTERNAL_LINK_REL } from './constants';
const IDE_TRACKING_EVENTS = {
    vscode: {
        ssh: OPEN_VSCODE_SSH,
        https: OPEN_VSCODE_HTTPS
    },
    intellij: {
        ssh: OPEN_INTELLIJ_SSH,
        https: OPEN_INTELLIJ_HTTPS
    }
};
export default {
    name: 'CECompactCodeDropdown',
    components: {
        GlLink,
        GlIcon,
        GlDisclosureDropdown,
        GlDisclosureDropdownGroup,
        CodeDropdownCloneItem,
        CodeDropdownDownloadItems,
        CodeDropdownIdeItem
    },
    mixins: [
        InternalEvents.mixin()
    ],
    props: {
        sshUrl: {
            type: String,
            required: false,
            default: ''
        },
        httpUrl: {
            type: String,
            required: false,
            default: ''
        },
        kerberosUrl: {
            type: String,
            required: false,
            default: null
        },
        xcodeUrl: {
            type: String,
            required: false,
            default: ''
        },
        webIdeUrl: {
            type: String,
            required: false,
            default: ''
        },
        gitpodUrl: {
            type: String,
            required: false,
            default: ''
        },
        currentPath: {
            type: String,
            required: false,
            default: null
        },
        directoryDownloadLinks: {
            type: Array,
            required: false,
            default: ()=>[]
        },
        showWebIdeButton: {
            type: Boolean,
            required: false,
            default: true
        },
        isGitpodEnabledForInstance: {
            type: Boolean,
            required: false,
            default: false
        },
        isGitpodEnabledForUser: {
            type: Boolean,
            required: false,
            default: false
        },
        showNoSshKeyMessage: {
            type: Boolean,
            required: false,
            default: false
        },
        userSettingsSshKeysPath: {
            type: String,
            required: false,
            default: ''
        },
        customClients: {
            type: Array,
            required: false,
            default: ()=>[]
        }
    },
    computed: {
        httpLabel () {
            const protocol = getHTTPProtocol(this.httpUrl)?.toUpperCase();
            return sprintf(__('Clone with %{protocol}'), {
                protocol
            });
        },
        sshUrlEncoded () {
            return encodeURIComponent(this.sshUrl);
        },
        httpUrlEncoded () {
            return encodeURIComponent(this.httpUrl);
        },
        webIdeActionShortcutKey () {
            return keysFor(GO_TO_PROJECT_WEBIDE)[0];
        },
        webIdeAction () {
            return {
                text: __('Web IDE'),
                shortcut: this.webIdeActionShortcutKey,
                tracking: {
                    action: 'click_consolidated_edit',
                    label: 'web_ide'
                },
                testId: 'webide-menu-item',
                href: this.webIdeUrl,
                extraAttrs: {
                    target: '_blank'
                }
            };
        },
        showGitpodButton () {
            return this.isGitpodEnabledForInstance && this.isGitpodEnabledForUser && this.gitpodUrl;
        },
        gitPodAction () {
            return {
                text: __('Ona'),
                tracking: {
                    action: 'click_consolidated_edit',
                    label: 'gitpod'
                },
                href: this.gitpodUrl,
                extraAttrs: {
                    target: '_blank'
                }
            };
        },
        ideGroup () {
            const actions = [];
            if (this.showWebIdeButton) actions.push(this.webIdeAction);
            if (this.showGitpodButton) actions.push(this.gitPodAction);
            if (this.httpUrl || this.sshUrl) {
                actions.push(this.createIdeGroup(__('Visual Studio Code'), VSCODE_BASE_URL, IDE_TYPE_VSCODE));
                actions.push(this.createIdeGroup(__('IntelliJ IDEA'), JETBRAINS_BASE_URL, IDE_TYPE_INTELLIJ));
            }
            if (this.xcodeUrl) {
                actions.push({
                    text: __('Xcode'),
                    href: this.xcodeUrl,
                    extraAttrs: {
                        isUnsafeLink: true,
                        rel: EXTERNAL_LINK_REL
                    }
                });
            }
            this.customClients.forEach((client)=>{
                const items = [];
                if (client.sshUrl) {
                    items.push({
                        text: __('SSH'),
                        href: client.sshUrl,
                        tracking: {
                            action: OPEN_CUSTOM_IDE_LINK,
                            additionalProperties: {
                                protocol: 'ssh'
                            }
                        },
                        extraAttrs: {
                            isUnsafeLink: true,
                            rel: EXTERNAL_LINK_REL
                        }
                    });
                }
                if (client.httpUrl) {
                    items.push({
                        text: __('HTTPS'),
                        href: client.httpUrl,
                        tracking: {
                            action: OPEN_CUSTOM_IDE_LINK,
                            additionalProperties: {
                                protocol: 'https'
                            }
                        },
                        extraAttrs: {
                            isUnsafeLink: true,
                            rel: EXTERNAL_LINK_REL
                        }
                    });
                }
                if (items.length > 0) {
                    actions.push({
                        text: client.name,
                        items
                    });
                }
            });
            return actions;
        },
        sourceCodeGroup () {
            return this.directoryDownloadLinks.map((link)=>({
                    text: link.text,
                    href: link.path,
                    extraAttrs: {
                        rel: 'nofollow',
                        download: ''
                    }
                }));
        },
        directoryDownloadGroup () {
            return this.directoryDownloadLinks.map((link)=>{
                const hrefEncoded = mergeUrlParams({
                    path: this.currentPath
                }, link.path);
                const href = hrefEncoded.replace(/%2F/g, '/');
                return {
                    text: link.text,
                    href,
                    extraAttrs: {
                        rel: 'nofollow',
                        download: ''
                    }
                };
            });
        },
        groups () {
            let firstVisibleGroup = null;
            return [
                // Important: the order of this array must match the order of the
                // GlDisclosureDropdownGroups in the template.
                [
                    'sshUrl',
                    this.sshUrl
                ],
                [
                    'httpUrl',
                    this.httpUrl
                ],
                [
                    'kerberosUrl',
                    this.kerberosUrl
                ],
                [
                    'ideGroup',
                    this.ideGroup.length > 0
                ],
                [
                    'downloadSourceCode',
                    this.directoryDownloadLinks.length > 0
                ],
                [
                    'downloadDirectory',
                    this.currentPath && this.directoryDownloadLinks.length > 0
                ]
            ].reduce((acc, [groupName, shouldShowGroup])=>{
                let bordered = Boolean(shouldShowGroup);
                if (!firstVisibleGroup && shouldShowGroup) {
                    firstVisibleGroup = groupName;
                    bordered = false;
                }
                acc[groupName] = {
                    show: shouldShowGroup,
                    bordered
                };
                return acc;
            }, {});
        },
        trackingData () {
            return {
                ssh: {
                    action: COPY_SSH_CLONE_URL
                },
                https: {
                    action: COPY_HTTPS_CLONE_URL
                }
            };
        }
    },
    methods: {
        onDropdownShown () {
            this.trackEvent(CODE_DROPDOWN_CLICK);
        },
        closeDropdown () {
            this.$refs.dropdown.close();
        },
        createIdeGroup (name, baseUrl, ideType) {
            return {
                text: name,
                items: [
                    ...this.sshUrl ? [
                        {
                            text: __('SSH'),
                            href: `${baseUrl}${this.sshUrlEncoded}`,
                            tracking: {
                                action: IDE_TRACKING_EVENTS[ideType].ssh
                            },
                            extraAttrs: {
                                isUnsafeLink: true,
                                rel: EXTERNAL_LINK_REL
                            }
                        }
                    ] : [],
                    ...this.httpUrl ? [
                        {
                            text: __('HTTPS'),
                            href: `${baseUrl}${this.httpUrlEncoded}`,
                            tracking: {
                                action: IDE_TRACKING_EVENTS[ideType].https
                            },
                            extraAttrs: {
                                isUnsafeLink: true,
                                rel: EXTERNAL_LINK_REL
                            }
                        }
                    ] : []
                ]
            };
        }
    }
};
